import { HttpErrorResponse } from '@angular/common/http'

import { HARNESS_ERROR_CODES } from '@flowx/designer/modules/ai-builder/types/harness.types'

/** The sentence a service's problem body carries in `message`, where the transport error only
 * has the status line; null when there is none. */
export function problemMessage(error: unknown): string | null {
  const message =
    error instanceof HttpErrorResponse
      ? (error.error as { message?: unknown } | null)?.message
      : null

  return typeof message === 'string' && message ? message : null
}

/** A failed call as the maker should read it: in the service's own words when its body has some. */
export function inServiceWords(error: unknown): unknown {
  const said = problemMessage(error)

  return said ? new Error(said) : error
}

/** The harness's code for a failed call or stream. Without one the stream is taken as cut short:
 * that is the failure that arrives without a body to say otherwise. */
export function harnessErrorCode(error: unknown): string {
  return readErrorField(error, 'code', HARNESS_ERROR_CODES.streamTruncated)
}

/** The harness's own sentence for a failed call or stream, shown as it is. */
export function harnessErrorMessage(error: unknown): string {
  return readErrorField(error, 'message', 'The harness could not be reached.')
}

/** A structured refusal's own code (`target_headers_required`, `context_moved`, …), when it had
 * one. */
export function harnessErrorReason(error: unknown): string | undefined {
  const reason = errorField(error, 'reason')

  return typeof reason === 'string' ? reason : undefined
}

/** The structured body a refusal carries beside its reason, when it carried one. */
export function harnessErrorDetail(error: unknown): Record<string, unknown> | undefined {
  const detail = errorField(error, 'detail')

  return detail && typeof detail === 'object' ? (detail as Record<string, unknown>) : undefined
}

/** The HTTP status a failed call came back with, when it reached the harness at all. */
export function harnessErrorHttpStatus(error: unknown): number | undefined {
  const status = errorField(error, 'status')

  return typeof status === 'number' ? status : undefined
}

function readErrorField(error: unknown, key: 'code' | 'message', fallback: string): string {
  const value = errorField(error, key)

  return typeof value === 'string' ? value : fallback
}

function errorField(error: unknown, key: string): unknown {
  return (error as Record<string, unknown> | null | undefined)?.[key]
}
